import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Heart, Users, Award, Star } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { useBookingLinks } from "../context/BookingLinksContext";
import { useSiteImages } from "../context/SiteImagesContext";

const ICONS = [Heart, Users, Award, Star];
const ICON_COLORS = ["#ff3131", "#7dbff8", "#ff3131", "#7dbff8"];

const AboutPage = () => {
  const [about, setAbout] = useState(null);
  const bookingLinks = useBookingLinks();
  const siteImages = useSiteImages();

  useEffect(() => {
    axios.get(`${API}/page/about`).then(res => setAbout(res.data)).catch(() => {});
  }, []);

  if (!about) return <div className="min-h-screen"><Header /><div className="py-20 text-center text-[#636E72]">Loading...</div><Footer /></div>;

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Header />
      <main>
        {/* Hero */}
        <section className="py-12 md:py-20 bg-gradient-to-b from-[#fff5f5] to-[#FFFBF5]">
          <div className="container-gymtots">
            <div className="max-w-3xl mx-auto text-center">
              <img src={siteImages["gymtots-logo.png"] || ""} alt="Gymtots" className="h-24 md:h-32 w-auto mx-auto mb-6" />
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
                Welcome to <span className="text-[#ff3131]">Gymtots</span>
              </h1>
              <p className="text-lg text-[#636E72] leading-relaxed">{about.intro}</p>
            </div>
          </div>
        </section>

        {/* Philosophy */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container-gymtots">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
                  {about.philosophy_title || "Our Philosophy"}
                </h2>
              </div>
              <div className="bg-[#FFFBF5] rounded-2xl p-6 md:p-8 border-l-4 border-[#ff3131]">
                {(about.philosophy_text || "").split("\n").filter(Boolean).map((p, i) => (
                  <p key={i} className="text-[#636E72] leading-relaxed mb-4 last:mb-0">{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Highlights */}
        {about.highlights && about.highlights.length > 0 && (
          <section className="py-12 md:py-16 bg-[#FFFBF5]">
            <div className="container-gymtots">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>
                  Why Choose <span className="text-[#ff3131]">Gymtots</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
                {about.highlights.map((h, i) => {
                  const Icon = ICONS[i % ICONS.length];
                  const color = ICON_COLORS[i % ICON_COLORS.length];
                  return (
                    <div key={i} className="bg-white rounded-2xl p-5 text-center">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${color}15` }}>
                        <Icon className="w-6 h-6" style={{ color }} />
                      </div>
                      <h3 className="font-bold text-[#2D3436] mb-1" style={{ fontFamily: 'Fredoka, cursive' }}>{h.title}</h3>
                      <p className="text-xs text-[#636E72]">{h.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Affiliations */}
        <section className="py-12 md:py-16 bg-[#1656a4]">
          <div className="container-gymtots">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-white/70 text-sm mb-6">Proudly Affiliated With</p>
              <div className="flex flex-wrap justify-center items-center gap-6 mb-8">
                <div className="bg-[#1656a4] rounded-xl p-4">
                  <img src={siteImages["dynamo-logo.png"] || ""} alt="Dynamo Gymnastics" className="h-12 md:h-16 w-auto" />
                </div>
                <div className="bg-white rounded-xl p-4">
                  <img src={siteImages["british-gymnastics-logo.webp"] || ""} alt="British Gymnastics" className="h-12 md:h-16 w-auto" />
                </div>
              </div>
              <p className="text-white/90 leading-relaxed max-w-2xl mx-auto">{about.affiliation_text}</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container-gymtots">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
                Ready to Get <span className="text-[#ff3131]">Started?</span>
              </h2>
              <p className="text-[#636E72] mb-6">Find the perfect class for your little one and book a trial session today!</p>
              <a href={bookingLinks.general || "#"} target="_blank" rel="noopener noreferrer" className="inline-block bg-[#ff3131] hover:bg-[#e62e2e] text-white px-8 py-4 rounded-full font-bold text-lg transition-colors" style={{ fontFamily: 'Fredoka, cursive' }}>
                Book a Trial Session!
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
